/**
 * «Stampa» del ticket (Centro Ticket, admin e rappresentante): utilità comuni
 * agli adapter per la risposta PDF (`responseType: 'blob'`) e ai suoi errori.
 */
import type { TicketPrintFile } from './supportApi';

/**
 * Attesa massima della richiesta del PDF (3 minuti): con molti allegati il
 * server (Chromium + pdf-lib) supera i 30 s di default di `axiosInstance`.
 */
export const PRINT_TIMEOUT_MS = 180000;

/** Nome del file da `Content-Disposition` (`filename*=UTF-8''…` o `filename="…"`); null se assente. */
export const filenameFromDisposition = (header: unknown): string | null => {
  if (typeof header !== 'string' || !header) return null;
  const star = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (star) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      // codifica non valida: si prova il nome semplice
    }
  }
  const plain = /filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;]+)/i.exec(header);
  const name = plain ? (plain[1] || plain[2] || '').trim() : '';
  return name || null;
};

interface BlobResponse {
  data: unknown;
  headers?: Record<string, unknown> | { get?: (name: string) => unknown };
}

const headerOf = (res: BlobResponse, name: string): unknown => {
  const h = res.headers as Record<string, unknown> & { get?: (n: string) => unknown };
  if (!h) return undefined;
  if (typeof h.get === 'function') {
    const v = h.get(name);
    if (v !== undefined && v !== null) return v;
  }
  return h[name] ?? h[name.toLowerCase()];
};

export const printFileFromResponse = (res: BlobResponse): TicketPrintFile => ({
  blob: res.data instanceof Blob ? res.data : new Blob([res.data as BlobPart], { type: 'application/pdf' }),
  filename: filenameFromDisposition(headerOf(res, 'content-disposition')),
});

export const printQuery = (o: { attachments: boolean; notes: boolean }): Record<string, string> => ({
  allegati: o.attachments ? '1' : '0',
  note: o.notes ? '1' : '0',
});

const blobText = (b: Blob): Promise<string> => {
  if (typeof b.text === 'function') return b.text();
  return new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve(typeof r.result === 'string' ? r.result : '');
    r.onerror = () => resolve('');
    r.readAsText(b);
  });
};

/**
 * Messaggio d'errore leggibile: con `responseType: 'blob'` il corpo JSON del
 * server arriva come Blob e va letto a parte.
 */
export const printErrorMessage = async (err: unknown): Promise<string> => {
  const fallback = 'Non è stato possibile preparare la stampa. Riprova.';
  const res = (err as { response?: { status?: number; data?: unknown } })?.response;
  if (!res) return fallback;
  if (res.status === 429) return 'Troppe stampe in poco tempo: riprova tra qualche minuto.';
  let data: unknown = res.data;
  try {
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
      const text = await blobText(data);
      data = text ? JSON.parse(text) : null;
    }
  } catch {
    data = null;
  }
  const msg = (data as { error?: unknown } | null)?.error;
  return typeof msg === 'string' && msg.trim() ? msg : fallback;
};
